import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu, X, Shield } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { BRAND } from '../lib/constants';

const NAV_LINKS = [
    { to: '/home', label: 'Interview' },
    { to: '/jobs', label: 'Jobs' },
    { to: '/history', label: 'History' },
    { to: '/stats', label: 'Statistics' },
    { to: '/profile', label: 'Profile' },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = React.useState(false);

    const onLogout = () => {
        logout();
        navigate('/login');
    };

    const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + '/');

    return (
        <div className="min-h-screen bg-white">
            {/* Top Navbar */}
            <header
                data-testid="top-navbar"
                className="sticky top-0 z-40 border-b border-[#E5E7EB] bg-white/95 backdrop-blur"
            >
                <div className="page-container flex h-16 items-center justify-between">
                    <Link to="/home" className="flex items-center gap-3" data-testid="brand-link">
                        <img
                            src={BRAND.logoUrl}
                            alt="ExcellenC"
                            className="h-9 w-auto"
                        />
                        <div className="hidden sm:block leading-none">
                            <div className="brand-wordmark text-[17px] leading-none">
                                {BRAND.name}
                            </div>
                            <div className="brand-wordmark-sub text-[11px] mt-0.5">
                                {BRAND.subtitle}
                            </div>
                        </div>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {NAV_LINKS.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-${l.label.toLowerCase()}`}
                                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                                    isActive(l.to)
                                        ? 'bg-[#1C3A94] text-white'
                                        : 'text-[#6B7280] hover:text-[#111827] hover:bg-gray-100'
                                }`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        {user?.role === 'admin' && (
                            <Link
                                to="/admin"
                                data-testid="nav-admin"
                                className={`ml-1 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
                                    isActive('/admin')
                                        ? 'bg-[#E52C2C] text-white'
                                        : 'text-[#E52C2C] hover:bg-[#FEF2F2]'
                                }`}
                            >
                                <Shield className="h-4 w-4" />
                                Admin
                            </Link>
                        )}
                    </nav>

                    <div className="hidden md:flex items-center gap-3">
                        {user && (
                            <div className="flex items-center gap-2">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#1C3A94] to-[#3552B8] text-sm font-semibold text-white">
                                    {(user.full_name || user.username || '?')[0].toUpperCase()}
                                </div>
                                <div className="hidden lg:block">
                                    <div className="text-xs font-semibold text-[#111827] leading-tight">
                                        {user.full_name || user.username}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                                        {user.role}
                                    </div>
                                </div>
                            </div>
                        )}
                        <button
                            data-testid="logout-btn"
                            onClick={onLogout}
                            className="brand-btn-ghost gap-1.5"
                            title="Logout"
                        >
                            <LogOut className="h-4 w-4" />
                            <span>Logout</span>
                        </button>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        className="md:hidden inline-flex items-center justify-center rounded-lg p-2 text-[#111827] hover:bg-gray-100"
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>

                {/* Mobile menu */}
                {mobileOpen && (
                    <div className="md:hidden border-t border-[#E5E7EB] bg-white">
                        <div className="page-container flex flex-col gap-1 py-3">
                            {NAV_LINKS.map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setMobileOpen(false)}
                                    className={`rounded-lg px-3 py-2 text-sm font-medium ${
                                        isActive(l.to)
                                            ? 'bg-[#1C3A94] text-white'
                                            : 'text-[#111827] hover:bg-gray-100'
                                    }`}
                                >
                                    {l.label}
                                </Link>
                            ))}
                            {user?.role === 'admin' && (
                                <Link
                                    to="/admin"
                                    onClick={() => setMobileOpen(false)}
                                    className="rounded-lg px-3 py-2 text-sm font-medium text-[#E52C2C] hover:bg-[#FEF2F2]"
                                >
                                    Admin Panel
                                </Link>
                            )}
                            <button
                                onClick={onLogout}
                                className="mt-2 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-[#6B7280] hover:bg-gray-100"
                            >
                                <LogOut className="h-4 w-4" /> Logout
                            </button>
                        </div>
                    </div>
                )}
            </header>

            <main className="page-container py-8">{children}</main>

            <footer className="mt-16 border-t border-[#E5E7EB] bg-[#F9FAFB]">
                <div className="page-container flex flex-col sm:flex-row items-center justify-between gap-2 py-6 text-xs text-[#6B7280]">
                    <div>© {new Date().getFullYear()} ExcellenC. All rights reserved.</div>
                    <div>PlacementPilot — powered by AI</div>
                </div>
            </footer>
        </div>
    );
}
